import { useState } from 'react'
import { useDebounceValue } from 'usehooks-ts'

import { useOrganisationProfiles } from '../../hooks/useOrganisationProfiles'
import { useTranslation } from '../../hooks/useTranslation'
import {
  OrganisationFilters,
  OrganisationFiltersValue,
} from '../OrganisationFilters/OrganisationFilters'
import { OrganisationTable } from '../OrganisationTable/OrganisationTable'

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 300
const MIN_QUERY_LENGTH = 3

export type OrganisationDirectoryProps = {
  // Partner hosts: adds the "donated to" switch (on by default) and last-donation column.
  withEngagement?: boolean
  // Pre-selects this country (alpha-3) until the user picks one; ignored while "donated to" is on.
  defaultCountryCode?: string | null
}

export function OrganisationDirectory({
  withEngagement = false,
  defaultCountryCode,
}: Readonly<OrganisationDirectoryProps>) {
  const { t } = useTranslation()

  const [filters, setFilters] = useState<OrganisationFiltersValue>({
    query: '',
    engaged: withEngagement ? true : undefined,
  })
  const [countryTouched, setCountryTouched] = useState(false)
  const [page, setPage] = useState(1)
  const [debouncedQuery] = useDebounceValue(
    filters.query.trim(),
    SEARCH_DEBOUNCE_MS
  )

  const { engaged } = filters
  const countryCode = countryTouched
    ? filters.countryCode
    : filters.countryCode ??
      (engaged ? undefined : defaultCountryCode ?? undefined)
  const searchQuery =
    debouncedQuery.length >= MIN_QUERY_LENGTH ? debouncedQuery : undefined
  const canSearch = !!engaged || !!searchQuery || !!countryCode

  const { data, isLoading } = useOrganisationProfiles(
    { engaged, query: searchQuery, countryCode, page, pageSize: PAGE_SIZE },
    { enabled: canSearch }
  )

  const emptyState = () => {
    if (!canSearch) {
      return {
        title: t('organisations.empty.searchTitle'),
        description: t('organisations.empty.searchDescription'),
      }
    }

    if (engaged && !searchQuery && !countryCode) {
      return {
        title: t('organisations.empty.engagedTitle'),
        description: t('organisations.empty.engagedDescription'),
      }
    }

    return {
      title: t('organisations.empty.noResultsTitle'),
      description: t('organisations.empty.noResultsDescription'),
    }
  }

  return (
    <OrganisationTable
      rows={canSearch ? data?.data ?? [] : []}
      totalResults={canSearch ? data?.totalResults ?? null : 0}
      exhaustiveTotalResults={
        !canSearch || data?.exhaustiveTotalResults !== false
      }
      isLoading={canSearch && isLoading}
      emptyState={emptyState()}
      page={page}
      pageSize={PAGE_SIZE}
      onPageChange={setPage}
      showLastEngagement={withEngagement}
      filters={
        <OrganisationFilters
          value={{ ...filters, countryCode }}
          onChange={(value) => {
            if (value.countryCode !== countryCode) {
              setCountryTouched(true)
            }
            setFilters(value)
            setPage(1)
          }}
        />
      }
    />
  )
}
